/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable no-nested-ternary */
import React, {
  useEffect,
  useMemo,
  useState,
  ForwardRefExoticComponent,
  RefAttributes,
} from 'react';
import {
  Button,
  Flex,
  Spinner,
  Text,
  SimpleGrid,
  Box,
  Card,
  CardBody,
  HStack,
  IconButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  ButtonGroup,
  Tooltip,
} from '@chakra-ui/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';
import { useQuery } from '@tanstack/react-query';
import { createSearchParams } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/pt-br';
import {
  NotePencil,
  FunnelSimple,
  FileText,
  ShoppingCart,
  File,
  Buildings,
  CaretUp,
  CaretDown,
  ArrowsDownUp,
  List,
  SquaresFour,
  Receipt,
  type IconProps,
} from 'phosphor-react';
import { MonthNavigator } from '../../../../components/shared/MonthNavigator';
import { useMonthFilter } from '../../../../hooks/useMonthFilter';
import { ReturnButton } from '../../../../components/buttons/ReturnButton';
import { InputForm } from '../../../../components/inputs/forms/InputForm';
import SelectInputForm from '../../../../components/inputs/forms/SelectInputForm';
import { IData, IPagination, ISelect } from '../../../../types/shared';
import { ICompany, ISaidaImport, useCompanies } from '../../../../hooks/useCompanies';
import { IClient } from '../../../../hooks/useClients';
import { getFormattedLabelString } from '../../../../utils/FormatFunctions';
import { cleanFilters } from '../../../../utils/HandleFunctions';
import { Paginator } from '../../../../components/paginator';
import { PageBox } from '../../../../components/pageBox';
import { FilterDrawer } from '../../../../components/FilterDrawer';
import { ManualPostingsCard } from '../../../../components/ManualPostingsCard';
import { ManualPostingsModal } from '../../../../components/ManualPostingsModal';

moment.locale('pt-br');

// Card de totais
interface TotalCardProps {
  title: string;
  value: number;
  icon: ForwardRefExoticComponent<IconProps & RefAttributes<SVGSVGElement>>;
  iconColor: string;
  bgColor: string;
  isLoading?: boolean;
  percentage?: number;
}

function TotalCard({
  title,
  value,
  icon: IconComponent,
  iconColor,
  bgColor,
  isLoading,
  percentage,
}: TotalCardProps) {
  if (isLoading) {
    return (
      <Card shadow="sm" bg="white" borderRadius="lg">
        <CardBody py={4} px={5}>
          <Flex align="center" justify="center" h="60px">
            <Spinner size="sm" color={iconColor} />
          </Flex>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card shadow="sm" bg="white" borderRadius="lg">
      <CardBody py={4} px={5}>
        <Flex align="center" gap={4}>
          <Box p={2} borderRadius="lg" bg={bgColor}>
            <IconComponent size={24} color={iconColor} weight="duotone" />
          </Box>
          <Box>
            <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
              {title}
            </Text>
            <Text fontSize="xl" fontWeight="bold" color="gray.800">
              {value.toLocaleString('pt-BR')}
            </Text>
            {percentage !== undefined && (
              <Text fontSize="xs" color="gray.500">
                {percentage.toFixed(2)}%
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
}

// Componente de cabeçalho ordenável
interface SortableHeaderProps {
  label: string;
  field: string;
  currentOrderBy: string | null;
  currentOrder: string | null;
  // eslint-disable-next-line no-unused-vars
  onSort: (field: string) => void;
  isNumeric?: boolean;
}

function SortableHeader({
  label,
  field,
  currentOrderBy,
  currentOrder,
  onSort,
  isNumeric = false,
}: SortableHeaderProps) {
  const isActive = currentOrderBy === field;

  const getSortIcon = () => {
    if (!isActive) {
      return <ArrowsDownUp size={14} weight="bold" color="#A0AEC0" />;
    }
    return currentOrder === 'desc' ? (
      <CaretDown size={14} weight="bold" color="#3182CE" />
    ) : (
      <CaretUp size={14} weight="bold" color="#3182CE" />
    );
  };

  return (
    <Th
      cursor="pointer"
      onClick={() => onSort(field)}
      _hover={{ bg: 'gray.100' }}
      userSelect="none"
      isNumeric={isNumeric}
      py={3}
      transition="background 0.2s"
    >
      <HStack spacing={1} justify={isNumeric ? 'flex-end' : 'flex-start'}>
        <Text fontSize="xs" fontWeight="semibold" color={isActive ? 'blue.600' : 'gray.600'}>
          {label}
        </Text>
        {getSortIcon()}
      </HStack>
    </Th>
  );
}

export function ModulesHomeIntegrationManualPostingsPage() {
  const { getCompaniesSaidasImports, getCompaniesFilters, getSaidasImportsTotais } = useCompanies();
  const {
    isOpen: isFilterDrawerOpen,
    onOpen: onFilterDrawerOpen,
    onClose: onFilterDrawerClose,
  } = useDisclosure();
  const { isOpen: isModalOpen, onOpen: onModalOpen, onClose: onModalClose } = useDisclosure();

  // Estado para modo de visualização (grid ou list)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');
  const [selectedItem, setSelectedItem] = useState<ISaidaImport | null>(null);

  // Estado para filtro de fatura ativo
  const [filterFaturaActive, setFilterFaturaActive] = useState(false);

  // Handler para clique no card
  const handleCardClick = (item: ISaidaImport) => {
    setSelectedItem(item);
    onModalOpen();
  };

  const {
    currentDate: currentFilterDate,
    dateStart,
    handlePrevMonth,
    handleNextMonth,
    handleSelectMonth,
    filters,
    setFilters,
  } = useMonthFilter({ additionalParams: { search: '', screen_data: 'manual_postings' } });

  const filtersFormMethods = useForm<{
    search: string;
    companies: ICompany;
    cliente: IClient;
    cidade: ISelect<string>;
    uf: ISelect<string>;
    grupo_de_empresas: ISelect<string>;
    tags: ISelect<string>;
    comercial: ISelect<string>;
    contabil: ISelect<string>;
    financeiro: ISelect<string>;
    contas_a_pagar: ISelect<string>;
    encerramento_anual: ISelect<string>;
    fiscal: ISelect<string>;
    imposto_de_renda: ISelect<string>;
    integracao_contabil: ISelect<string>;
    integracao_fiscal: ISelect<string>;
    pessoal: ISelect<string>;
    relacionamento_com_cliente: ISelect<string>;
    societario: ISelect<string>;
  }>({
    mode: 'onChange',
    defaultValues: {
      search: filters.get('search') ?? '',
    },
  });

  // Estado atual de ordenação
  const currentOrderBy = filters.get('order_by');
  const currentOrder = filters.get('order');

  // Função para alternar ordenação (clique no cabeçalho da coluna)
  const handleColumnSort = (field: string) => {
    const newFilters = new URLSearchParams(filters);

    if (currentOrderBy === field) {
      // Se já está ordenando por este campo, alterna a direção
      if (currentOrder === 'desc') {
        newFilters.set('order', 'asc');
        newFilters.set('order_by', field);
      } else {
        // Se está em asc, remove a ordenação
        newFilters.delete('order');
        newFilters.delete('order_by');
      }
    } else {
      // Novo campo: começa com desc (maior primeiro)
      newFilters.set('order', 'desc');
      newFilters.set('order_by', field);
    }

    newFilters.set('page', '1');
    setFilters(newFilters);
  };

  // Debounce para search
  const debouncedSearch = useDebounceCallback((searchValue: string) => {
    const newFilters = new URLSearchParams(filters);
    if (searchValue) {
      newFilters.set('search', searchValue);
    } else {
      newFilters.delete('search');
    }
    newFilters.set('page', '1');
    setFilters(newFilters);
  }, 500);

  useEffect(() => {
    const subscription = filtersFormMethods.watch((values, { name }) => {
      if (name === 'search') {
        debouncedSearch(values.search || '');
      }
    });

    return () => subscription.unsubscribe();
  }, [filtersFormMethods.watch, debouncedSearch]);

  // Função para aplicar os filtros avançados quando clica no botão "Filtrar"
  const handleApplyFilters = () => {
    const values = filtersFormMethods.getValues();
    const newFilters = new URLSearchParams();

    // Mantém filtros básicos
    newFilters.set('page', '1');
    newFilters.set('size', '50');
    if (filters.get('search')) newFilters.set('search', filters.get('search') || '');
    newFilters.set('screen_data', 'manual_postings');
    newFilters.set(
      'date_start',
      filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
    );
    newFilters.set(
      'date_end',
      filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
    );

    // Mantém order se existir
    const order = filters.get('order');
    const orderBy = filters.get('order_by');
    if (order) newFilters.set('order', order);
    if (orderBy) newFilters.set('order_by', orderBy);

    // Aplica filtros avançados
    if (values.companies?.external_id) newFilters.set('id', values.companies.external_id);
    if (values.cidade?.value) newFilters.set('cidade', values.cidade.value);
    if (values.uf?.value) newFilters.set('uf', values.uf.value);
    if (values.grupo_de_empresas?.value)
      newFilters.set('grupo_de_empresas', values.grupo_de_empresas.value);
    if (values.tags?.value) newFilters.set('tags', values.tags.value);
    if (values.comercial?.value) newFilters.set('comercial', values.comercial.value);
    if (values.contabil?.value) newFilters.set('contabil', values.contabil.value);
    if (values.financeiro?.value) newFilters.set('financeiro', values.financeiro.value);
    if (values.contas_a_pagar?.value) newFilters.set('contas_a_pagar', values.contas_a_pagar.value);
    if (values.encerramento_anual?.value)
      newFilters.set('encerramento_anual', values.encerramento_anual.value);
    if (values.fiscal?.value) newFilters.set('fiscal', values.fiscal.value);
    if (values.imposto_de_renda?.value)
      newFilters.set('imposto_de_renda', values.imposto_de_renda.value);
    if (values.integracao_contabil?.value)
      newFilters.set('integracao_contabil', values.integracao_contabil.value);
    if (values.integracao_fiscal?.value)
      newFilters.set('integracao_fiscal', values.integracao_fiscal.value);
    if (values.pessoal?.value) newFilters.set('pessoal', values.pessoal.value);
    if (values.relacionamento_com_cliente?.value)
      newFilters.set('relacionamento_com_cliente', values.relacionamento_com_cliente.value);
    if (values.societario?.value) newFilters.set('societario', values.societario.value);

    setFilters(newFilters);
  };

  const queryKeyCompaniesFilters = ['CompaniesFilters'];
  const { data: CompaniesFiltersData } = useQuery({
    queryKey: queryKeyCompaniesFilters,
    queryFn: getCompaniesFilters,
  });

  const CompaniesFilters = CompaniesFiltersData;

  // Adicionar filter_fatura ao filtro quando ativo
  const filtersWithFatura = useMemo(() => {
    const baseFilters = cleanFilters(Object.fromEntries([...Array.from(filters)]));
    if (filterFaturaActive) {
      return { ...baseFilters, filter_fatura: 'true' };
    }
    return baseFilters;
  }, [filters, filterFaturaActive]);

  const queryKeyCompaniesList = ['CompaniesList-manualPostings', filtersWithFatura];
  const { data: dataCompaniesList, isFetching: isFetchingCompanyList } = useQuery<
    IData<ISaidaImport[]>
  >({
    queryKey: queryKeyCompaniesList,
    queryFn: getCompaniesSaidasImports,
  });

  const { items: companiesList, ...pagination } = dataCompaniesList?.data ?? {
    items: [],
    ...({} as IPagination),
  };

  // Criar filtros para totais
  const filtersForTotals = useMemo(() => {
    const totalsFilters = new URLSearchParams(Object.fromEntries(filters));
    return Object.fromEntries([...Array.from(totalsFilters)]);
  }, [filters]);

  const queryKeyTotais = ['getTotais-manualPostings', filtersForTotals];

  const { data: totais, isFetching: isFetchingTotais } = useQuery<any>({
    queryKey: queryKeyTotais,
    queryFn: getSaidasImportsTotais,
  });

  // Calcular filtros ativos para indicadores visuais
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.get('id')) count += 1;
    if (filters.get('cidade')) count += 1;
    if (filters.get('uf')) count += 1;
    if (filters.get('grupo_de_empresas')) count += 1;
    if (filters.get('tags')) count += 1;
    if (filters.get('comercial')) count += 1;
    if (filters.get('contabil')) count += 1;
    if (filters.get('financeiro')) count += 1;
    if (filters.get('contas_a_pagar')) count += 1;
    if (filters.get('encerramento_anual')) count += 1;
    if (filters.get('fiscal')) count += 1;
    if (filters.get('imposto_de_renda')) count += 1;
    if (filters.get('integracao_contabil')) count += 1;
    if (filters.get('integracao_fiscal')) count += 1;
    if (filters.get('pessoal')) count += 1;
    if (filters.get('relacionamento_com_cliente')) count += 1;
    if (filters.get('societario')) count += 1;
    return count;
  }, [filters]);

  const hasActiveFilters = activeFiltersCount > 0;

  const handleClearFilters = () => {
    const clearedFilters = createSearchParams({
      page: '1',
      size: '50',
      search: filters.get('search') || '',
      date_start: filters.get('date_start') || moment().startOf('month').format('YYYY-MM-DD'),
      date_end: filters.get('date_end') || moment().endOf('month').format('YYYY-MM-DD'),
      screen_data: 'manual_postings',
    });
    // Mantém order se existir
    const order = filters.get('order');
    const orderBy = filters.get('order_by');
    if (order) clearedFilters.set('order', order);
    if (orderBy) clearedFilters.set('order_by', orderBy);

    setFilters(clearedFilters);

    // Limpar cada campo individualmente para forçar atualização visual
    filtersFormMethods.setValue('companies', undefined as any);
    filtersFormMethods.setValue('cidade', undefined as any);
    filtersFormMethods.setValue('uf', undefined as any);
    filtersFormMethods.setValue('grupo_de_empresas', undefined as any);
    filtersFormMethods.setValue('tags', undefined as any);
    filtersFormMethods.setValue('comercial', undefined as any);
    filtersFormMethods.setValue('contabil', undefined as any);
    filtersFormMethods.setValue('financeiro', undefined as any);
    filtersFormMethods.setValue('contas_a_pagar', undefined as any);
    filtersFormMethods.setValue('encerramento_anual', undefined as any);
    filtersFormMethods.setValue('fiscal', undefined as any);
    filtersFormMethods.setValue('imposto_de_renda', undefined as any);
    filtersFormMethods.setValue('integracao_contabil', undefined as any);
    filtersFormMethods.setValue('integracao_fiscal', undefined as any);
    filtersFormMethods.setValue('pessoal', undefined as any);
    filtersFormMethods.setValue('relacionamento_com_cliente', undefined as any);
    filtersFormMethods.setValue('societario', undefined as any);
  };

  // Configuração de cards de totais
  const cardConfig = {
    nfe: {
      icon: FileText,
      iconColor: '#219653',
      bgColor: '#E8F5E9',
    },
    nfce: {
      icon: ShoppingCart,
      iconColor: '#F97316',
      bgColor: '#FFF3E0',
    },
    nfse: {
      icon: File,
      iconColor: '#9333EA',
      bgColor: '#F3E5F5',
    },
    fatura: {
      icon: Receipt,
      iconColor: '#0891B2',
      bgColor: '#E0F7FA',
    },
    total: {
      icon: Buildings,
      iconColor: '#6B7280',
      bgColor: '#F5F5F5',
    },
  };

  return (
    <PageBox>
      {/* Header com título */}
      <div className="flex content-start pb-5 md:w-1/3 lg:content-center">
        <ReturnButton previousPage="modulesHome/integration" />

        <div className="h-full">
          <div className="flex items-center justify-center rounded-full bg-[#D9F1F1] p-3">
            <NotePencil color="#37474F" className="size-8" />
          </div>
        </div>
        <div className="flex w-full flex-col justify-center gap-1 pl-2">
          <Text fontWeight="bold" fontSize="16px">
            Lançamentos Manuais
          </Text>
          <Text fontSize="12px" color="gray.500">
            Visualize os lançamentos manuais por empresa
          </Text>
        </div>
      </div>

      {/* Cards de Totais */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
        <TotalCard
          title="Total NFE"
          value={totais?.quantity_nfe ?? 0}
          icon={cardConfig.nfe.icon}
          iconColor={cardConfig.nfe.iconColor}
          bgColor={cardConfig.nfe.bgColor}
          isLoading={isFetchingTotais}
          percentage={totais?.percent_nfe}
        />
        <TotalCard
          title="Total NFCE"
          value={totais?.quantity_nfce ?? 0}
          icon={cardConfig.nfce.icon}
          iconColor={cardConfig.nfce.iconColor}
          bgColor={cardConfig.nfce.bgColor}
          isLoading={isFetchingTotais}
          percentage={totais?.percent_nfce}
        />
        <TotalCard
          title="Total NFSE"
          value={totais?.quantity_nfse ?? 0}
          icon={cardConfig.nfse.icon}
          iconColor={cardConfig.nfse.iconColor}
          bgColor={cardConfig.nfse.bgColor}
          isLoading={isFetchingTotais}
          percentage={totais?.percent_nfse}
        />
        {/* Card especial de Fatura com tooltip e clique */}
        <Tooltip
          label={
            totais?.fatura_periodo_inicio && totais?.fatura_periodo_fim
              ? `Dados do período filtrado (${moment(totais.fatura_periodo_inicio).format('DD/MM/YYYY')} a ${moment(totais.fatura_periodo_fim).format('DD/MM/YYYY')}). Clique para filtrar usuários com notas de fatura.`
              : 'Empresas que possuem apenas notas de fatura no período filtrado. Clique para filtrar.'
          }
          hasArrow
          placement="top"
        >
          <Card
            shadow="sm"
            bg={filterFaturaActive ? 'cyan.100' : 'white'}
            borderRadius="lg"
            cursor="pointer"
            onClick={() => setFilterFaturaActive(!filterFaturaActive)}
            border="2px"
            borderColor={filterFaturaActive ? 'cyan.500' : 'transparent'}
            transition="all 0.2s"
            _hover={{ shadow: 'md', borderColor: 'cyan.300' }}
          >
            <CardBody py={4} px={5}>
              {isFetchingTotais ? (
                <Flex align="center" justify="center" h="60px">
                  <Spinner size="sm" color={cardConfig.fatura.iconColor} />
                </Flex>
              ) : (
                <Flex align="center" gap={4}>
                  <Box p={2} borderRadius="lg" bg={cardConfig.fatura.bgColor}>
                    <Receipt size={24} color={cardConfig.fatura.iconColor} weight="duotone" />
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500" fontWeight="medium" mb={1}>
                      Fatura
                    </Text>
                    <HStack spacing={4}>
                      <Box>
                        <Text fontSize="lg" fontWeight="bold" color="gray.800">
                          {(totais?.empresas_apenas_fatura ?? 0).toLocaleString('pt-BR')}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          empresas
                        </Text>
                      </Box>
                      <Box borderLeft="1px" borderColor="gray.200" pl={4}>
                        <Text fontSize="lg" fontWeight="bold" color="gray.800">
                          {(totais?.total_notas_fatura ?? 0).toLocaleString('pt-BR')}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          notas
                        </Text>
                      </Box>
                    </HStack>
                  </Box>
                </Flex>
              )}
            </CardBody>
          </Card>
        </Tooltip>
        <TotalCard
          title="Total Geral"
          value={
            (totais?.quantity_nfe ?? 0) +
            (totais?.quantity_nfce ?? 0) +
            (totais?.quantity_nfse ?? 0)
          }
          icon={cardConfig.total.icon}
          iconColor={cardConfig.total.iconColor}
          bgColor={cardConfig.total.bgColor}
          isLoading={isFetchingTotais}
        />
      </SimpleGrid>

      {/* Indicador de filtro de fatura ativo */}
      {filterFaturaActive && (
        <Box mb={4} p={3} bg="cyan.50" borderRadius="md" border="1px" borderColor="cyan.200">
          <Flex justify="space-between" align="center">
            <Text fontSize="sm" color="cyan.700">
              Filtrando usuários que importaram notas de <strong>Fatura</strong> no período filtrado
              {totais?.fatura_periodo_inicio && totais?.fatura_periodo_fim && (
                <>
                  {' '}
                  ({moment(totais.fatura_periodo_inicio).format('DD/MM/YYYY')} a{' '}
                  {moment(totais.fatura_periodo_fim).format('DD/MM/YYYY')})
                </>
              )}
            </Text>
            <Button
              size="xs"
              colorScheme="cyan"
              variant="outline"
              onClick={() => setFilterFaturaActive(false)}
            >
              Limpar filtro
            </Button>
          </Flex>
        </Box>
      )}

      {/* Filtros no topo */}
      <Box mb={6} p={4} bg="gray.50" borderRadius="lg">
        <FormProvider {...filtersFormMethods}>
          {/* Linha principal: Pesquisa + Ordenação + Navegação de Mês + Botão Filtros */}
          <Flex
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align={{ base: 'stretch', md: 'center' }}
            gap={4}
          >
            {/* Campo de Pesquisa */}
            <Box flex={1} maxW={{ base: '100%', md: '350px' }}>
              <InputForm
                name="search"
                placeholder="Buscar usuário..."
                boxShadow="0px 2px 4px #1E293B0D"
                controlMb={0}
                height="38px"
              />
            </Box>

            {/* Toggle Grid/List, Navegação de Mês e Botão Filtros */}
            <HStack spacing={3} flexWrap="wrap">
              {/* Toggle Grid/List */}
              <ButtonGroup size="sm" isAttached variant="outline">
                <Tooltip label="Visualização em lista">
                  <IconButton
                    aria-label="Modo lista"
                    icon={<List size={18} />}
                    onClick={() => setViewMode('list')}
                    colorScheme={viewMode === 'list' ? 'blue' : 'gray'}
                    variant={viewMode === 'list' ? 'solid' : 'outline'}
                  />
                </Tooltip>
                <Tooltip label="Visualização em grade">
                  <IconButton
                    aria-label="Modo grade"
                    icon={<SquaresFour size={18} />}
                    onClick={() => setViewMode('grid')}
                    colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
                    variant={viewMode === 'grid' ? 'solid' : 'outline'}
                  />
                </Tooltip>
              </ButtonGroup>

              <MonthNavigator
                currentDate={currentFilterDate}
                onPrevMonth={handlePrevMonth}
                onNextMonth={handleNextMonth}
                onSelectMonth={handleSelectMonth}
              />
              <Button
                onClick={onFilterDrawerOpen}
                leftIcon={<FunnelSimple size={16} />}
                variant="outline"
                colorScheme={hasActiveFilters ? 'blue' : 'gray'}
                height="38px"
              >
                Filtros {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
            </HStack>
          </Flex>

          {/* FilterDrawer para filtros avançados */}
          <FilterDrawer
            isOpen={isFilterDrawerOpen}
            onClose={onFilterDrawerClose}
            title="Filtros Avançados"
            hasActiveFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
            onApplyFilters={handleApplyFilters}
            formMethods={filtersFormMethods}
            size="lg"
          >
            <div className="grid grid-cols-2 gap-4">
              <SelectInputForm<ISelect<string>>
                name="cidade"
                placeholder="Filtrar por Cidade"
                options={getFormattedLabelString(CompaniesFilters?.cidade)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="uf"
                placeholder="Filtrar por Estado"
                options={getFormattedLabelString(CompaniesFilters?.uf)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="grupo_de_empresas"
                placeholder="Filtrar por Grupo de Empresa"
                options={getFormattedLabelString(CompaniesFilters?.grupo_de_empresas)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="tags"
                placeholder="Filtrar por Tags"
                options={getFormattedLabelString(CompaniesFilters?.tags)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="comercial"
                placeholder="Filtrar por Comercial"
                options={getFormattedLabelString(CompaniesFilters?.comercial)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="contabil"
                placeholder="Filtrar por Contábil"
                options={getFormattedLabelString(CompaniesFilters?.contabil)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="financeiro"
                placeholder="Filtrar por Financeiro"
                options={getFormattedLabelString(CompaniesFilters?.financeiro)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="contas_a_pagar"
                placeholder="Filtrar por Contas a Pagar"
                options={getFormattedLabelString(CompaniesFilters?.contas_a_pagar)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="encerramento_anual"
                placeholder="Filtrar por Encerramento Anual"
                options={getFormattedLabelString(CompaniesFilters?.encerramento_anual)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="fiscal"
                placeholder="Filtrar por Fiscal"
                options={getFormattedLabelString(CompaniesFilters?.fiscal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="imposto_de_renda"
                placeholder="Filtrar por Imposto de Renda"
                options={getFormattedLabelString(CompaniesFilters?.imposto_de_renda)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="integracao_contabil"
                placeholder="Filtrar por Integração Contábil"
                options={getFormattedLabelString(CompaniesFilters?.integracao_contabil)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="integracao_fiscal"
                placeholder="Filtrar por Integração Fiscal"
                options={getFormattedLabelString(CompaniesFilters?.integracao_fiscal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="pessoal"
                placeholder="Filtrar por Pessoal"
                options={getFormattedLabelString(CompaniesFilters?.pessoal)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="relacionamento_com_cliente"
                placeholder="Filtrar por Relacionamento com Cliente"
                options={getFormattedLabelString(CompaniesFilters?.relacionamento_com_cliente)}
                controlMb={0}
              />
              <SelectInputForm<ISelect<string>>
                name="societario"
                placeholder="Filtrar por Societário"
                options={getFormattedLabelString(CompaniesFilters?.societario)}
                controlMb={0}
              />
            </div>
          </FilterDrawer>
        </FormProvider>
      </Box>

      {/* Lançamentos - Grid ou List */}
      {isFetchingCompanyList ? (
        <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={4} py={8}>
          <Spinner size="lg" color="primary" />
          <Text fontSize="xl" fontWeight="semibold">
            Carregando...
          </Text>
        </Flex>
      ) : companiesList?.length === 0 ? (
        <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={4} py={8}>
          <Text fontSize="lg" color="gray.500">
            Nenhum lançamento encontrado
          </Text>
        </Flex>
      ) : viewMode === 'grid' ? (
        /* Modo Grid */
        <Box>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3, xl: 4 }} spacing={4}>
            {companiesList?.map(item => (
              <ManualPostingsCard key={item.id} item={item} onClick={handleCardClick} />
            ))}
          </SimpleGrid>

          {/* Paginação */}
          <Box mt={4}>
            <Paginator pagination={pagination} />
          </Box>
        </Box>
      ) : (
        /* Modo List (Tabela) */
        <Box>
          <TableContainer
            bg="white"
            borderRadius="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
            maxH="calc(100vh - 280px)"
            overflowY="auto"
          >
            <Table variant="simple" size="md">
              <Thead bg="gray.50" position="sticky" top={0} zIndex={1} boxShadow="sm">
                <Tr>
                  <Th py={3} fontSize="xs" color="gray.600">
                    Usuário
                  </Th>
                  <Th py={3} fontSize="xs" color="gray.600">
                    Origem
                  </Th>
                  <SortableHeader
                    label="NFE"
                    field="nfe"
                    currentOrderBy={currentOrderBy}
                    currentOrder={currentOrder}
                    onSort={handleColumnSort}
                    isNumeric
                  />
                  <SortableHeader
                    label="NFCE"
                    field="nfce"
                    currentOrderBy={currentOrderBy}
                    currentOrder={currentOrder}
                    onSort={handleColumnSort}
                    isNumeric
                  />
                  <SortableHeader
                    label="NFSE"
                    field="nfse"
                    currentOrderBy={currentOrderBy}
                    currentOrder={currentOrder}
                    onSort={handleColumnSort}
                    isNumeric
                  />
                  {filterFaturaActive && (
                    <SortableHeader
                      label="Fatura"
                      field="fatura"
                      currentOrderBy={currentOrderBy}
                      currentOrder={currentOrder}
                      onSort={handleColumnSort}
                      isNumeric
                    />
                  )}
                  <SortableHeader
                    label="Total"
                    field="total"
                    currentOrderBy={currentOrderBy}
                    currentOrder={currentOrder}
                    onSort={handleColumnSort}
                    isNumeric
                  />
                </Tr>
              </Thead>
              <Tbody>
                {companiesList?.map(item => (
                  <Tr
                    key={item.id}
                    _hover={{ bg: 'blue.50', cursor: 'pointer' }}
                    transition="background 0.2s"
                    onClick={() => handleCardClick(item)}
                  >
                    <Td py={3}>
                      <Text fontWeight="medium" fontSize="sm" color="gray.800">
                        {item?.name}
                      </Text>
                    </Td>
                    <Td py={3}>
                      <Text fontSize="sm" color="gray.500">
                        {item?.origin}
                      </Text>
                    </Td>
                    <Td isNumeric py={3}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {(item.quantity_nfe ?? 0).toLocaleString('pt-BR')}
                      </Text>
                    </Td>
                    <Td isNumeric py={3}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {(item.quantity_nfce ?? 0).toLocaleString('pt-BR')}
                      </Text>
                    </Td>
                    <Td isNumeric py={3}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.700">
                        {(item.quantity_nfse ?? 0).toLocaleString('pt-BR')}
                      </Text>
                    </Td>
                    {filterFaturaActive && (
                      <Td isNumeric py={3}>
                        <Text fontSize="sm" fontWeight="medium" color="cyan.600">
                          {(item.quantity_fatura ?? 0).toLocaleString('pt-BR')}
                        </Text>
                      </Td>
                    )}
                    <Td isNumeric py={3}>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                        {(item.quantity_total ?? 0).toLocaleString('pt-BR')}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          <Box mt={4}>
            <Paginator pagination={pagination} />
          </Box>
        </Box>
      )}

      {/* Modal de detalhes */}
      <ManualPostingsModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        item={selectedItem}
        initialDateStart={dateStart}
      />
    </PageBox>
  );
}
